import {
  DermloopReferralTypeEnum,
  REFERRAL_STATUS_FILTERS,
  ReferralStatus,
  type ReferralStatusFilter,
  ReferralWorkflow,
} from '@dermloop/static-values';
import {
  IsArray,
  IsBoolean,
  IsDate,
  IsDateString,
  IsEnum,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxDate,
  Min,
  MinDate,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../base.dto';
import { UserDto } from '../user.dto';
import { ReferralClaimDto } from './referral-claim.dtos';
import type { ReferralResponseDto } from './referral-response.dtos';
import type { TeledermatologicalAssessmentDto } from './teledermatological-assessment.dtos';
import type { ConditionTriageDto } from '../condition';

export class CreateReferralDto {
  @IsOptional()
  @IsEnum(DermloopReferralTypeEnum)
  referralType?: DermloopReferralTypeEnum;

  @IsNotEmpty()
  @IsString()
  medicalUnitId: string;

  @IsOptional()
  @IsBoolean()
  urgent?: boolean;

  /**
   * Routing key, denormalised onto the referral (NOT NULL). Rich flow copies it
   * from `conditionType.speciality`; simple flow picks it in the form.
   */
  @IsNotEmpty()
  @IsUUID()
  specialityId: string;

  /** Direct patient link (NOT NULL) — see the entity for why it is denormalised. */
  @IsNotEmpty()
  @IsUUID()
  healthRecordId: string;

  /** Workflow shape. Required so every referral is created with an explicit flow. */
  @IsEnum(ReferralWorkflow)
  workflow: ReferralWorkflow;
}

export class UpdateReferralDto {
  @IsOptional()
  @IsEnum(DermloopReferralTypeEnum)
  referralType?: DermloopReferralTypeEnum;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  medicalUnitId: string;

  @IsOptional()
  @IsBoolean()
  urgent?: boolean;
}

export class ReferralDto implements Partial<BaseDto> {
  // If null, this dto can be used for referral creation
  // If not null, this dto can be used for referral update
  id?: string;
  createdByUser?: UserDto;
  updatedByUser?: UserDto;
  deletedByUser?: UserDto;
  created?: string | Date;
  updated?: string | Date;
  deleted?: string | Date;

  @IsOptional()
  @IsEnum(DermloopReferralTypeEnum)
  referralType?: DermloopReferralTypeEnum;

  @IsOptional()
  @IsNotEmpty()
  latestTeledermatologicalAssessment?: TeledermatologicalAssessmentDto;

  latestTeledermatologicalAssessmentId?: string;

  @IsNotEmpty()
  @IsString()
  medicalUnitId: string;

  @IsOptional()
  @IsBoolean()
  urgent?: boolean;

  referralClaim?: ReferralClaimDto;

  @IsOptional()
  @ValidateNested()
  conditionTriage?: ConditionTriageDto;

  @IsOptional()
  @ValidateNested()
  /** @deprecated Use conditionTriage */
  skinConditionTriage?: ConditionTriageDto;

  @IsOptional()
  @IsBoolean()
  wouldReferPhysicallyIfTeleUnavailable?: boolean;

  /**
   * The authoritative specialist answer: the newest response whose submission is
   * no longer a draft. Absent while nobody has answered.
   */
  latestCompletedResponse?: ReferralResponseDto;

  /**
   * The authenticated caller's own open draft response, so they can resume it.
   * Never another specialist's draft, and absent when the read carries no caller.
   */
  ownDraftResponse?: ReferralResponseDto;

  /**
   * Derived from InsuranceInfoDto
   */
  clinicalQuestion?: string;
  released?: boolean;
}

export class ReferralStatisticsDto {
  // Date str should be in the format yyyy-MM-dd
  @IsDateString()
  toDateStr: string;

  @IsOptional()
  @IsDateString()
  fromDateStr?: string;

  @IsOptional()
  @IsUUID()
  federationId?: string;
}

interface ReferralStatisticsPendingDto {
  // Total pending referrals
  totalPendingReferrals: number;
  // How many referrals are pending, that are between 24 and 48 hours old
  totalPendingReferrals24Hours: number;
  // How many referrals are pending, that are between 48 and 96 hours old
  totalPendingReferrals48Hours: number;
  // How many referrals are pending, that are older than 96 hours
  totalPendingReferrals96Hours: number;
}

export interface DailyStatistic {
  count: number;
  isoCreatedDate: string; // ISO date string
}

export interface ReferralStatisticsResDto {
  overall: ReferralStatisticsPendingDto;
  jurisdictional: (ReferralStatisticsPendingDto & {
    jurisdictionalGroupId: string;
    jurisdictionalGroupName: string;
  })[];
  dailyNewReferrals: {
    fromDateStr: string;
    toDateStr: string;
    data: DailyStatistic[];
  };
}

/**
 * Filters for the admin overview of unanswered referrals. The boolean filters
 * are arrays because they are driven by multi-select table columns, where
 * selecting every option is equivalent to selecting none.
 */
export class UnansweredReferralSearchDto {
  @IsOptional()
  @IsInt()
  @Min(0)
  skip?: number;

  /** The paging helper clamps anything above 50, so reject it up front. */
  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(50)
  take?: number;

  /** Ordering of the created column. Defaults to newest first. */
  @IsOptional()
  @IsIn(['ASC', 'DESC'])
  createdOrder?: 'ASC' | 'DESC';

  /**
   * Calendar day bounds (`yyyy-MM-dd`), both inclusive. A full timestamp is
   * accepted too and is truncated to its day. The day is the API server's —
   * referral timestamps carry no timezone, so a day resolves against server
   * time (UTC in production), not the caller's local day.
   */
  @IsOptional()
  @IsDateString()
  createdFrom?: string;

  @IsOptional()
  @IsDateString()
  createdTo?: string;

  /** Free-text, case-insensitive substring match on the medical unit name. */
  @IsOptional()
  @IsString()
  medicalUnitName?: string;

  @IsOptional()
  @IsArray()
  @IsEnum(DermloopReferralTypeEnum, { each: true })
  referralTypes?: DermloopReferralTypeEnum[];

  @IsOptional()
  @IsArray()
  @IsBoolean({ each: true })
  isDraft?: boolean[];

  @IsOptional()
  @IsArray()
  @IsBoolean({ each: true })
  released?: boolean[];

  /** Calendar day bounds, as for the created range. */
  @IsOptional()
  @IsDateString()
  releasedFrom?: string;

  @IsOptional()
  @IsDateString()
  releasedTo?: string;

  @IsOptional()
  @IsArray()
  @IsBoolean({ each: true })
  releasedToAllowedSpecialistGroupMembers?: boolean[];

  @IsOptional()
  @IsArray()
  @IsIn([...REFERRAL_STATUS_FILTERS], { each: true })
  statuses?: ReferralStatusFilter[];

  @IsOptional()
  @IsArray()
  @IsBoolean({ each: true })
  isAssigned?: boolean[];
}

/**
 * Operational state of a referral that has not been answered, as shown in the
 * admin overview. Deliberately carries no patient identity and no clinical
 * content — the overview exists to diagnose stuck referral flow, not to read
 * cases.
 */
export interface UnansweredReferralDto {
  id: string;
  /** The eConsult number. */
  idNumber: number;
  /** Ids only, for linking to the case; they carry no patient identity. */
  healthRecordId: string | null;
  /** Only rich-workflow referrals have one, through their triage. */
  conditionId: string | null;
  created: string | Date;
  /** Null when the referral's medical unit has been deleted. */
  medicalUnitName: string | null;
  referralType: DermloopReferralTypeEnum | null;
  /** Null when the referral has neither a triage nor a form submission. */
  isDraft: boolean | null;
  released: boolean;
  releasedDate: string | Date | null;
  /** The column is nullable; a null is reported as false. */
  releasedToAllowedSpecialistGroupMembers: boolean;
  /** Null means open. */
  status: ReferralStatus | null;
  isAssigned: boolean;
  /** Set when the assignment resolves to a user, if that user has a name. */
  assigneeName: string | null;
  assigneeEmail: string | null;
  /** Set when the assignment resolves to a medical unit rather than a user. */
  assignedMedicalUnitName: string | null;
  /**
   * Only rich-workflow referrals can have one: the encounter hangs off the
   * triage and is itself optional. Always projected, in every region — the
   * admin overview hides the columns outside the US, but nothing strips the
   * fields from the response.
   */
  encounterFhirReference: string | null;
  orderFhirReference: string | null;
}
